import React from 'react';
import { Container, Alert, Button, Card } from 'react-bootstrap';
import { useRouteError, useNavigate } from 'react-router-dom';
import { ApiError } from '@/common/types/errors/ApiError.ts';

interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
  data?: string;
  stack?: string;
}

const ErrorBoundary: React.FC = () => {
  const error = useRouteError() as RouteError | Error;
  const navigate = useNavigate();

  // Extract error message from different error types
  const getErrorMessage = (): string => {
    const fallback = 'An unexpected error occurred';

    if (error instanceof ApiError) {
      return error.message || fallback;
    }

    if (error instanceof Error) {
      return error.message || fallback;
    }

    if (error instanceof Response) {
      return error.statusText || fallback;
    }

    const routeError = error as RouteError;
    return routeError.message || routeError.statusText || fallback;
  };

  // Extract error status
  const getErrorStatus = (): number | undefined => {
    if (error instanceof ApiError) {
      return error.status;
    }

    const routeError = error as RouteError;
    return routeError.status;
  };

  // Get user-friendly error content based on status and message
  const getErrorContent = () => {
    const status = getErrorStatus();
    const rawMessage = getErrorMessage();

    switch (status) {
      case 400:
        return {
          title: 'Bad Request',
          message:
            'The request was invalid. Please check your input and try again.',
          variant: 'danger' as const,
        };

      case 401:
        return {
          title: 'Authentication Required',
          message:
            'You need to log in to access this resource. Please sign in and try again.',
          variant: 'warning' as const,
        };

      case 403:
        return {
          title: 'Access Denied',
          message:
            "You don't have permission to access this resource. Contact support if you believe this is an error.",
          variant: 'warning' as const,
        };

      case 404:
        return {
          title: 'Page Not Found',
          message:
            "The page you're looking for doesn't exist. It may have been moved or deleted.",
          variant: 'danger' as const,
        };

      case 409:
        return {
          title: 'Conflict',
          message:
            'There was a conflict with your request. The resource may already exist or be in use.',
          variant: 'warning' as const,
        };

      case 422:
        return {
          title: 'Validation Error',
          message:
            'The information provided was invalid. Please check your input and try again.',
          variant: 'warning' as const,
        };

      case 429:
        return {
          title: 'Too Many Requests',
          message:
            "You've made too many requests. Please wait a moment and try again.",
          variant: 'warning' as const,
        };

      case 500:
        return {
          title: 'Server Error',
          message:
            "We're experiencing technical difficulties. Please try again in a few minutes.",
          variant: 'danger' as const,
        };

      case 502:
      case 503:
      case 504:
        return {
          title: 'Service Unavailable',
          message:
            'The service is temporarily unavailable. Please try again later.',
          variant: 'warning' as const,
        };

      default: {
        // For unknown errors, use the raw message if it's user-friendly, otherwise use generic message
        const isUserFriendly =
          rawMessage &&
          !rawMessage.includes('Error:') &&
          !rawMessage.includes('Exception');
        return {
          title: status ? `Error ${status}` : 'Application Error',
          message: isUserFriendly
            ? rawMessage
            : 'An unexpected error occurred. Please try again.',
          variant: 'danger' as const,
        };
      }
    }
  };

  const { title, message, variant } = getErrorContent();
  const status = getErrorStatus();

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const isServerError = status && status >= 500;
  const isClientError = status && status >= 400 && status < 500;

  return (
    <Container className="py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <Card className="shadow-sm">
            <Card.Body className="text-center p-4">
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <img
                    src="/images/afh-logo-small.webp"
                    alt="Aspen Functional Health"
                    className="me-3"
                    style={{ maxHeight: '60px', width: 'auto' }}
                  />
                  <h3 className="mb-0 text-primary fw-bold">
                    Aspen Functional Health
                  </h3>
                </div>
                <div>
                  <i
                    className={`bi ${isServerError ? 'bi-server' : isClientError ? 'bi-exclamation-triangle' : 'bi-bug'} text-danger`}
                    style={{ fontSize: '3rem' }}
                  ></i>
                </div>
              </div>

              <Card.Title as="h1" className="h3 mb-3">
                {title}
              </Card.Title>

              <Alert variant={variant} className="text-start">
                <Alert.Heading className="h6">What happened?</Alert.Heading>
                <p className="mb-0">{message}</p>
              </Alert>

              <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                <Button
                  variant="primary"
                  onClick={handleReload}
                  className="me-md-2"
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Try Again
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={handleGoBack}
                  className="me-md-2"
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Go Back
                </Button>
              </div>

              {isServerError && (
                <Alert variant="info" className="mt-4 text-start">
                  <Alert.Heading className="h6">
                    <i className="bi bi-info-circle me-2"></i>
                    What can you do?
                  </Alert.Heading>
                  <ul className="mb-0">
                    <li>Try refreshing the page</li>
                    <li>Check back in a few minutes</li>
                    <li>Contact support if the problem persists</li>
                  </ul>
                </Alert>
              )}

              {status === 401 && (
                <Alert variant="info" className="mt-4 text-start">
                  <Alert.Heading className="h6">
                    <i className="bi bi-info-circle me-2"></i>
                    Next Steps
                  </Alert.Heading>
                  <p className="mb-0">
                    Please sign in to your account and try again. If you don't
                    have an account, contact an administrator.
                  </p>
                </Alert>
              )}

              <div className="mt-4 pt-3 border-top">
                <p className="text-muted small mb-2">
                  Need help? Contact our support team:
                </p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() =>
                    (window.location.href =
                      'mailto:info@aspenfunctionalhealth.com?subject=Portal Error Report')
                  }
                  className="text-decoration-none"
                >
                  <i className="bi bi-envelope me-1"></i>
                  info@aspenfunctionalhealth.com
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default ErrorBoundary;
